<template lang="pug">
control
  field(:class="{ invalid: (!start.meta.valid && start.meta.touched) || (!end.meta.valid && end.meta.touched) }")
    label(:id="global.labelId") {{ start.label }}
    p(v-html="start.label")
    div
      input(
        @blur="start.blur",
        @keydown.up.prevent="start.increase",
        @keydown.down.prevent="start.decrease",
        v-model="start.model",
        :type="start.type",
        :inputmode="start.inputmode",
        :aria-labelledby="global.labelId",
        :name="start.id",
        :id="start.id",
        :placeholder="start.placeholder",
      )
      error(v-if="!start.meta.valid && start.meta.touched") {{ start.errorMessage }}
    p {{ end.label }}
    div
      input(
        @blur="end.blur",
        @keydown.up.prevent="end.increase",
        @keydown.down.prevent="end.decrease",
        v-model="end.model",
        :type="end.type",
        :inputmode="end.inputmode",
        :aria-labelledby="global.labelId",
        :name="end.id",
        :id="end.id",
        :placeholder="end.placeholder",
      )
      error(v-if="!end.meta.valid && end.meta.touched") {{ end.errorMessage }}
</template>

<script>
import { useStore } from 'vuex';
import { reactive, ref, watch } from 'vue';
import { useField } from 'vee-validate';
import * as yup from 'yup';

export default {
  name: 'Range',
  props: {
    field: Object,
  },
  setup(p) {
    const store = useStore();
    const start = reactive(initStart());
    const end = reactive(initEnd());
    const global = reactive(initGlobal());

    return { global, start, end };

    function initGlobal() {
      return { labelId: p.field.id };
    }

    function initStart() {
      let labelHtml = p.field.label;
      if (labelHtml && p.field.required !== false) labelHtml = `<b>${labelHtml}</b>`;

      const id = `${p.field.id}-start`;
      const label = labelHtml ? `${labelHtml}${p.field.required !== false ? " <span class='required'>*</span>" : ""}` : null;
      const type = 'number';
      const inputmode = "decimal";

      let rules = yup.number()
        .positive()
        .nullable()

      if (p.field.required !== false) {
        rules = rules.required();
      }

      const { value, errorMessage, meta, handleBlur, validate } = useField(
        id,
        rules,
        {
          validateOnInput: true,
          initialValue: store.getters.model[p.field.id]?.[0],
        }
      );

      const model = ref(/*fixed*/);
      const initial = ref(true);

      watch(
        () => store.getters.model[p.field.id]?.[0] || '',
        (v) => (model.value = v)
      );

      watch(model, (v) => {
        if (Object.hasOwn(store.getters.model, p.field.id) || initial.value) {
          if (!v) {
            if (model.value !== v) {
              model.value = v;
            }
            value.value = undefined;
          }
          else {
            v = Math.round((v + Number.EPSILON) * 100) / 100;
            if (model.value !== v) {
              model.value = v;
            }
            value.value = v;
          }
          initial.value = false;
          store.commit("updateModel", { id: p.field.id, index: 0, value: v });
        }
      });

      model.value = store.getters.model[p.field.id]?.[0];

      const increase = () => {
        let value = model.value || 0;
        model.value = ++value;
      };

      const decrease = () => {
        let value = model.value || 0;
        if (value >= 1) {
          model.value = --value;
        }
      };

      const blur = async () => {
        store.commit('pushLastFieldToDataLayer', id);
        await validate();
        handleBlur();
      };

      const placeholder = p.field.required === false
        ? ""
        : "(Required)";

      return { id, label, type, model, inputmode, meta, blur, increase, decrease, errorMessage, placeholder };
    }

    function initEnd() {
      const id = `${p.field.id}-end`;
      const label = p.field.rangetext ? p.field.rangetext : null;
      const type = 'number';
      const inputmode = "decimal";

      let rules = yup.number()
        .nullable()
        .positive()

      if (p.field.required !== false) {
        rules = rules.required();
      }

      const { value, errorMessage, meta, handleBlur, validate } = useField(
        id,
        rules,
        {
          validateOnInput: true,
          initialValue: store.getters.model[p.field.id]?.[1],
        }
      );

      const model = ref(/*fixed*/);
      const initial = ref(true);

      watch(
        () => store.getters.model[p.field.id]?.[1] || '',
        (v) => (model.value = v)
      );

      watch(model, (v) => {
        if (Object.hasOwn(store.getters.model, p.field.id) || initial.value) {
          if (!v) {
            model.value = "";
            value.value = undefined;
          }
          else {
            v = Math.round((v + Number.EPSILON) * 100) / 100;
            if (model.value !== v) {
              model.value = v;
            }
            value.value = v;
          }
          initial.value = false;
          store.commit("updateModel", { id: p.field.id, index: 1, value: v });
        }
      });

      model.value = store.getters.model[p.field.id]?.[1];

      const increase = () => {
        let value = model.value || 0;
        model.value = ++value;
      };

      const decrease = () => {
        let value = model.value || 0;
        if (value >= 1) {
          model.value = --value;
        }
      };

      const blur = async () => {
        store.commit('pushLastFieldToDataLayer', id);
        await validate();
        handleBlur();
      };

      const placeholder = p.field.required === false
        ? ""
        : "(Required)";

      return { id, label, type, model, inputmode, meta, blur, increase, decrease, errorMessage, placeholder };
    }
  },
};
</script>

<style lang="scss" scoped>
label {
  @include hideVisually;
}

form-control {
  form-field {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;

    &>div {
      display: flex;
      flex-direction: column;
    }

    p {
      margin-bottom: 1rem;
    }

    input[type=number] {
      border: none;
      border-bottom: 1px solid $slate;
      border-radius: 0;
      margin-bottom: 1rem;
      text-align: center;
    }
  }
}
</style>
